import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { IMCMapView } from '../../../classes/mcMapView';
import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { ImportService } from '../../../services/import.service';
import { LayerService } from '../../../services/layer.service';
import { ILayerSettings } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-add-external-layer-ng',
    templateUrl: './add-external-layer.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './add-external-layer.component.scss']
})
export class AddExternalLayerComponent extends BaseComponent implements AfterViewInit {

    @Input() mapViewRef: IMCMapView;
    layerUrl: string;
    layerTitle: string;
    layerFilter: string;
    layerResult;

    loadingInd: boolean = false;
    warnMsg: string = '';

    fetchSuccess = false;
    externalLayerFilterEnabled = environment.externalLayerFilterEnabled;
    dynamicMapServiceResult;

    @ViewChild('fileInput', { read: ElementRef }) private readonly fileInput: ElementRef;

    @Output() readonly onreset: EventEmitter<null> = new EventEmitter<null>();
    @Output() readonly onclose: EventEmitter<null> = new EventEmitter<null>();

    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        public readonly importService: ImportService,
        private readonly httpClient: HttpClient) {
        super(_vcr, true);
        this.registerEvent('onreset', this.onreset);
        this.registerEvent('onclose', this.onclose);
    }
    async ngAfterViewInit() {
        this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
        this.isReady();
    }

    async addToMap() {
        if (this.layerResult) {

            try {
                const layerProps = await this.layerService.getExternalServiceProperties({ url: this.layerResult.url });
                await this.layerService.addExternalLayersToMap(layerProps);
            } catch (error) {
                this.warnMsg = 'Error adding external layer to map';
            }

        } else if (this.dynamicMapServiceResult) {
            this.prepareExternalLayer(this.dynamicMapServiceResult);
            await this.addMapImageLayerWithSelectedLayers();
            delete this.dynamicMapServiceResult.layers;
        }
        this.close();
    }

    async addMapImageLayerWithSelectedLayers() {
        const addPromises: Promise<__esri.FeatureLayer>[] = [];
        for (const layer of this.dynamicMapServiceResult.layers.filter(l => l.visible)) {
            const properties: ILayerSettings = { url: `${this.dynamicMapServiceResult.url}/${layer.id}` };
            addPromises.push(this.layerService.addExternalLayersToMap(properties, { url: properties.url, name: layer.name }));
        }
        await Promise.all(addPromises);
    }

    prepareExternalLayer(layer) {
        layer.filter = this.layerFilter;
        layer.displayName = this.layerTitle;
        layer.isWMS = this.isWMS();
    }

    resetForm() {
        this.layerUrl = null;
        this.dynamicMapServiceResult = null;
        this.layerResult = null;
        this.fetchSuccess = false;
        this.layerFilter = null;
        this.warnMsg = '';
        this.onreset.emit();
    }

    async fetchUrl() {
        this.warnMsg = '';
        this.loadingInd = true;
        this.layerResult = null;
        this.fetchSuccess = false;
        this.dynamicMapServiceResult = null;

        this.layerUrl = this.layerUrl.trim();

        // if mapcore is loaded over https, convert external layer to https to prevent mixed-content error.
        if (window.location.href.indexOf('https:') >= 0) {
            if (this.layerUrl.indexOf('http:') >= 0) {
                this.layerUrl = this.layerUrl.replace(/^http:/, 'https:');
            }
        }

        if (this.isWMS()) {
            await this.fetchWMSServiceCapabilities({ url: this.layerUrl });
        } else if (/^https?:\/\/.*\/[0-9]{1,}$/.test(this.layerUrl)) {
            await this.fetchFeatureLayerSymbols();
        } else {
            await this.fetchDynamicServiceLayerList({ url: this.layerUrl });
        }

    }

    isWMS() {
        return (this.layerUrl && (this.layerUrl.indexOf('WMSServer') > 0 || this.layerUrl.indexOf('SERVICE=WMS') > 0));
    }

    public async fetchFeatureLayerSymbols() {
        // get the info about the service to find out if it accepts advanced queries
        try {
            const serviceProps = await this.layerService.getExternalServiceProperties({ url: this.layerUrl });
            const layerResult = await this.layerService.getExternalLayerSymbolData({ url: this.layerUrl });
            this.loadingInd = false;
            if (serviceProps.supportsAdvancedQueries) {
                layerResult.supportsAdvancedQueries = serviceProps.supportsAdvancedQueries; // does the service support selecting distinct values
            }
            if (layerResult) {
                this.warnMsg = '';
                this.layerTitle = layerResult.name || layerResult.displayName || '';
                this.layerResult = layerResult;
                this.fetchSuccess = true;
            } else {
                this.warnMsg = 'Layer Not Found.';
                this.layerResult = null;
            }
        } catch {
            this.warnMsg = 'Error loading layer URL.';
        }
    }

    public async fetchDynamicServiceLayerList(layer) {
        const esriRequest = (await ESRI.Request) as any;
        try {
            const response = await esriRequest(`${layer.url}?f=json`, {
                responseType: 'json'
            });
            this.dynamicMapServiceResult = response.data;
            this.dynamicMapServiceResult.url = layer.url;
            if (this.dynamicMapServiceResult.documentInfo && this.dynamicMapServiceResult.documentInfo.Title) {
                this.layerTitle = this.dynamicMapServiceResult.documentInfo.Title;
            } else {
                const urlParts = layer.url.split('/');
                const partLength = 3;
                const extraLength = 2;
                if (urlParts.length > partLength) {
                    this.layerTitle = urlParts[urlParts.length - extraLength];
                }
            }
            this.fetchSuccess = true;
        }
        catch {
            this.warnMsg = 'Error loading layer URL.';
        }
        this.loadingInd = false;
    }

    public fetchWMSServiceCapabilities(layer) {
        this.dynamicMapServiceResult = {};
        this.dynamicMapServiceResult.layers = [];
        const questionMarkIndex = layer.url.indexOf('?');
        if (questionMarkIndex > 0) {
            layer.url = layer.url.substring(0, layer.url.indexOf('?'));
        }
        const url = `${environment.gisSearch.baseUrl}/proxy?${layer.url}?SERVICE=WMS&REQUEST=GetCapabilities`;
        this.httpClient.get(url, { withCredentials: true }).subscribe((result: any) => {
            if (result.data && (<any>result.data).result && (<any>result.data).result.Message) {
                this.warnMsg = (<any>result.data).result.Message;
            } else if (result.data) {
                const httpResult = result.data;
                const xml = (new (window as any).DOMParser()).parseFromString(httpResult, 'text/xml');
                const layerCollection = xml.getElementsByTagName('Layer');
                if (layerCollection) {
                    const layers = Array.prototype.slice.call(layerCollection);
                    layers.forEach((lyr) => {
                        const titles = lyr.getElementsByTagName('Title');
                        const names = lyr.getElementsByTagName('Name');
                        const nameLen = 2;
                        if (titles.length && titles.length <= nameLen && names.length && names.length <= nameLen) {
                            const title = titles[0];
                            const name = names[0];
                            if (name && name.textContent && title && title.textContent) {
                                lyr.id = parseInt(name.textContent, 0);
                                lyr.name = `${name.textContent}  ${title.textContent}`;
                            }
                            this.dynamicMapServiceResult.layers.push(lyr);
                        }
                    });
                    this.fetchSuccess = true;
                } else {
                    this.warnMsg = 'No layer information found for this WMS Service';
                }
            } else {
                this.warnMsg = 'Error retrieving WMS Service Capabilities';
            }
            this.loadingInd = false;

        });
    }

    inputFile() {
        const inputElement: HTMLElement = this.fileInput.nativeElement as HTMLElement;
        (<any>this.fileInput).files = [];
        inputElement.click();
    }

    public async fileChangeEvent(e: Event) {
        const inputFileElement = e.target as HTMLInputElement;
        await this.importService.importFiles(e);
        inputFileElement.value = ''; // Helps force change if same file selected again.
        this.close();
    }

    close() {
        this.resetForm();
        this.onclose.emit();
    }

}
